import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { searchApi } from '../../api/search'
import { coursesApi } from '../../api/courses'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Button from '../../components/common/Button'
import Loading from '../../components/common/Loading'
import { FiSearch, FiBook, FiBookOpen, FiAward, FiChevronLeft, FiChevronRight, FiUser } from 'react-icons/fi'
import { getStorageUrl } from '../../utils/constants'
import './Search.css'

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const authContext = useAuth()
  const isAuthenticated = authContext?.isAuthenticated ?? false
  
  // Initialize state from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '')
  const [selectedUniversity, setSelectedUniversity] = useState(searchParams.get('university') || '')
  const [selectedFaculty, setSelectedFaculty] = useState(searchParams.get('faculty') || '')
  const [selectedTerm, setSelectedTerm] = useState(searchParams.get('term') || '')
  const [selectedSubject, setSelectedSubject] = useState(searchParams.get('subject') || '')
  const [hasSearched, setHasSearched] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 12

  // Initialize hasSearched from URL params
  useEffect(() => {
    const hasParams = searchParams.get('q') || searchParams.get('subject') || searchParams.get('university')
    setHasSearched(!!hasParams)
  }, [searchParams])

  const { data: universitiesData } = useQuery({
    queryKey: ['universities'],
    queryFn: () => searchApi.getUniversities(),
    enabled: !!isAuthenticated, // Only fetch if authenticated
  })

  const { data: facultiesData } = useQuery({
    queryKey: ['faculties', selectedUniversity],
    queryFn: () => searchApi.getFaculties(selectedUniversity),
    enabled: !!selectedUniversity && !!isAuthenticated,
  })

  const { data: termsData } = useQuery({
    queryKey: ['terms', selectedFaculty],
    queryFn: () => searchApi.getTerms(selectedFaculty),
    enabled: !!selectedFaculty && !!isAuthenticated,
  })

  const { data: subjectsData } = useQuery({
    queryKey: ['subjects', selectedTerm],
    queryFn: () => searchApi.getSubjects(selectedTerm),
    enabled: !!selectedTerm && !!isAuthenticated,
  })

  // Build search params object
  const searchParams_obj = {}
  if (searchQuery.trim()) {
    searchParams_obj.search = searchQuery.trim()
  }
  if (selectedSubject) {
    searchParams_obj.subject_id = selectedSubject
  }
  if (selectedUniversity) {
    searchParams_obj.university_id = selectedUniversity
  }
  if (selectedTerm) {
    searchParams_obj.semester_id = selectedTerm // Backend uses semester_id for term
  }

  // Only search if user has submitted or has URL params
  // Allow search even with just text query (no filters needed)
  const shouldSearch = !!(hasSearched && (searchQuery.trim() || selectedSubject || selectedUniversity || selectedTerm || selectedFaculty))

  const { data: coursesData, isLoading } = useQuery({
    queryKey: ['courses-search', searchQuery, selectedSubject, selectedUniversity, selectedTerm],
    queryFn: () => coursesApi.getCourses(searchParams_obj),
    enabled: shouldSearch, // Only search when user has submitted
  })

  const handleSearch = (e) => {
    if (e) e.preventDefault()
    setHasSearched(true)
    setCurrentPage(1) // Reset to first page on new search
    
    // Update URL params
    const params = {}
    if (searchQuery.trim()) params.q = searchQuery.trim()
    if (selectedUniversity) params.university = selectedUniversity
    if (selectedFaculty) params.faculty = selectedFaculty
    if (selectedTerm) params.term = selectedTerm
    if (selectedSubject) params.subject = selectedSubject
    
    setSearchParams(params)
  }

  const universities = universitiesData?.data?.universities || []
  const faculties = facultiesData?.data?.faculties || []
  const terms = termsData?.data?.terms || []
  const subjects = subjectsData?.data?.subjects || []
  const courses = coursesData?.data?.courses || coursesData?.data?.data?.courses || []

  // Pagination logic
  const totalPages = Math.ceil(courses.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedCourses = courses.slice(startIndex, endIndex)

  // Get icon for course type
  const getCourseIcon = (course) => {
    // You can customize this based on course type or category
    if (course.category?.toLowerCase().includes('exam')) return FiAward
    if (course.category?.toLowerCase().includes('book')) return FiBookOpen
    return FiBook
  }

  // Get instructor count (if available)
  const getInstructorCount = (course) => {
    if (course.instructors && Array.isArray(course.instructors)) {
      return course.instructors.length
    }
    if (course.instructor) return 1
    return 0
  }

  return (
    <div className="search-page bg-gray-900 min-h-screen">
      <Header />
      <main className="search-main bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <h1 className="page-title text-white">البحث المتقدم</h1>
          
          <form onSubmit={handleSearch} className="search-form">
            {/* Search Bar with Button */}
            <div className="search-bar-container">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  className="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="بحث عن ماده في المنصة"
                />
              </div>
              <button type="submit" className="search-button">
                <FiSearch />
              </button>
            </div>

            {/* Category Filter Buttons */}
            {subjects.length > 0 && (
              <div className="category-filters">
                {subjects.slice(0, 5).map((subject) => (
                  <button
                    key={subject.id}
                    type="button"
                    className={`category-filter-btn ${selectedSubject === subject.id.toString() ? 'active' : ''}`}
                    onClick={() => {
                      const newSubject = selectedSubject === subject.id.toString() ? '' : subject.id.toString()
                      setSelectedSubject(newSubject)
                      setCurrentPage(1)
                      setHasSearched(true)
                      // Trigger search
                      const params = {}
                      if (searchQuery.trim()) params.q = searchQuery.trim()
                      if (selectedUniversity) params.university = selectedUniversity
                      if (selectedFaculty) params.faculty = selectedFaculty
                      if (selectedTerm) params.term = selectedTerm
                      if (newSubject) params.subject = newSubject
                      setSearchParams(params)
                    }}
                  >
                    {subject.name}
                  </button>
                ))}
              </div>
            )}

            {isAuthenticated ? (
              <div className="search-filters">
                <div className="filter-group">
                  <label>الجامعة</label>
                  <select
                    value={selectedUniversity}
                    onChange={(e) => {
                      setSelectedUniversity(e.target.value)
                      setSelectedFaculty('')
                      setSelectedTerm('')
                      setSelectedSubject('')
                    }}
                  >
                    <option value="">اختر الجامعة</option>
                    {universities.map((uni) => (
                      <option key={uni.id} value={uni.id}>
                        {uni.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedUniversity && (
                  <div className="filter-group">
                    <label>الكلية</label>
                    <select
                      value={selectedFaculty}
                      onChange={(e) => {
                        setSelectedFaculty(e.target.value)
                        setSelectedTerm('')
                        setSelectedSubject('')
                      }}
                    >
                      <option value="">اختر الكلية</option>
                      {faculties.map((faculty) => (
                        <option key={faculty.id} value={faculty.id}>
                          {faculty.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedFaculty && (
                  <div className="filter-group">
                    <label>الفصل</label>
                    <select
                      value={selectedTerm}
                      onChange={(e) => {
                        setSelectedTerm(e.target.value)
                        setSelectedSubject('')
                      }}
                    >
                      <option value="">اختر الفصل</option>
                      {terms.map((term) => (
                        <option key={term.id} value={term.id}>
                          {term.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedTerm && (
                  <div className="filter-group">
                    <label>المادة</label>
                    <select
                      value={selectedSubject}
                      onChange={(e) => setSelectedSubject(e.target.value)}
                    >
                      <option value="">اختر المادة</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-message">
                <p>يرجى تسجيل الدخول لاستخدام الفلاتر المتقدمة</p>
              </div>
            )}
          </form>

          {shouldSearch && (
            <>
              {isLoading ? (
                <Loading text="جاري البحث..." />
              ) : (
                <div className="search-results">
                  <h2 className="results-title">
                    نتائج البحث ({courses.length})
                  </h2>
                  {courses.length > 0 ? (
                    <>
                      <div className="courses-grid">
                        {paginatedCourses.map((course) => {
                          const CourseIcon = getCourseIcon(course)
                          const instructorCount = getInstructorCount(course)
                          
                          return (
                            <Link
                              key={course.id}
                              to={`/courses/${course.id}`}
                              className="search-result-card"
                            >
                              <div className="search-result-icon-wrapper">
                                <CourseIcon className="search-result-icon" />
                              </div>
                              <div className="search-result-content">
                                <h3 className="search-result-title">{course.title}</h3>
                                <p className="search-result-subtitle">
                                  {course.instructor?.name || course.category || 'دورة تعليمية'}
                                </p>
                                {instructorCount > 0 && (
                                  <div className="search-result-status">
                                    <span>({instructorCount} مدرس)</span>
                                    <span className="status-dot"></span>
                                    <span className="status-text">موجود</span>
                                  </div>
                                )}
                              </div>
                            </Link>
                          )
                        })}
                      </div>
                      
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="pagination">
                          <button
                            className="pagination-button"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                          >
                            <FiChevronRight />
                          </button>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              className={`pagination-button ${currentPage === page ? 'active' : ''}`}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            className="pagination-button"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                          >
                            <FiChevronLeft />
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="empty-state">
                      <p>لا توجد نتائج</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {!shouldSearch && (
            <div className="search-prompt">
              <p>استخدم البحث أعلاه للعثور على الدورات</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Search

